import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "prevBtn", "nextBtn"]

  connect() {
    this.currentOffset = 0
    this.itemWidth = 80 // 80px card (w-20 = 5rem = 80px), no gap between items
    this.visibleItems = this.calculateVisibleItems()

    this.updateButtons()

    // Recalculate on resize
    this.boundHandleResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.boundHandleResize)

    // Recalculate when modal becomes visible (observer for visibility changes)
    this.setupVisibilityObserver()
  }

  disconnect() {
    window.removeEventListener('resize', this.boundHandleResize)
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.dateTrackObserver) {
      this.dateTrackObserver.disconnect()
    }
  }

  setupVisibilityObserver() {
    // Watch for the modal becoming visible to recalculate dimensions
    const modal = this.element.closest('.modal')
    if (modal) {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            // Modal visibility changed - recalculate after a small delay for layout to settle
            if (!modal.classList.contains('hidden')) {
              requestAnimationFrame(() => {
                this.recalculate()
              })
            }
          }
        })
      })
      this.observer.observe(modal, { attributes: true, attributeFilter: ['class'] })
    }

    // Also watch for date track children changes (dates being populated)
    if (this.hasContainerTarget) {
      const dateTrack = this.containerTarget.querySelector('[data-activity-modal-target="dateTrack"]')
      if (dateTrack) {
        this.dateTrackObserver = new MutationObserver(() => {
          // Dates were populated - recalculate
          requestAnimationFrame(() => {
            this.currentOffset = 0 // Reset to start when dates change
            this.recalculate()
          })
        })
        this.dateTrackObserver.observe(dateTrack, { childList: true })
      }
    }
  }

  recalculate() {
    this.visibleItems = this.calculateVisibleItems()
    const maxOffset = this.getMaxOffset()
    if (this.currentOffset > maxOffset) {
      this.currentOffset = Math.max(0, maxOffset)
    }
    this.updateTransform()
    this.updateButtons()
  }

  handleResize() {
    this.recalculate()
  }

  calculateVisibleItems() {
    if (!this.hasContainerTarget) return 4

    const containerWidth = this.containerTarget.offsetWidth
    // If container width is 0 (hidden), return a default
    if (containerWidth === 0) return 4

    return Math.floor(containerWidth / this.itemWidth)
  }

  getTotalItems() {
    if (!this.hasContainerTarget) return 0
    return this.containerTarget.querySelector('[data-activity-modal-target="dateTrack"]')?.children.length || 0
  }

  getMaxOffset() {
    const total = this.getTotalItems()
    return Math.max(0, (total - this.visibleItems) * this.itemWidth)
  }

  previous(event) {
    event.preventDefault()

    const scrollAmount = this.itemWidth * 2 // Scroll 2 items at a time
    this.currentOffset = Math.max(0, this.currentOffset - scrollAmount)
    this.updateTransform()
    this.updateButtons()
  }

  next(event) {
    event.preventDefault()

    const maxOffset = this.getMaxOffset()
    const scrollAmount = this.itemWidth * 2 // Scroll 2 items at a time
    this.currentOffset = Math.min(maxOffset, this.currentOffset + scrollAmount)
    this.updateTransform()
    this.updateButtons()
  }

  updateTransform() {
    const track = this.containerTarget?.querySelector('[data-activity-modal-target="dateTrack"]')
    if (track) {
      track.style.transform = `translateX(-${this.currentOffset}px)`
    }
  }

  updateButtons() {
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.disabled = this.currentOffset <= 0
    }

    if (this.hasNextBtnTarget) {
      const maxOffset = this.getMaxOffset()
      this.nextBtnTarget.disabled = this.currentOffset >= maxOffset
    }
  }
}
